/**
 * The admin portal's API client.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAME ORIGIN, SEPARATE ORIGIN — AND BOTH HALVES MATTER.
 * ─────────────────────────────────────────────────────────────────────────────
 * Requests go to RELATIVE paths (`/auth/login`, `/api/me`). Caddy's
 * `admin.{$RAAD_DOMAIN}` vhost proxies `/auth/*` and `/api/*` to `raad-api`
 * verbatim — `handle`, not `handle_path` — so the upstream sees the same paths
 * it sees from `app.`, and the refresh cookie's `Path=/auth` matches the
 * external URL exactly. That is the `AUTH_COOKIE_PATH` seam slice 04 got right
 * by naming it and slice 05 got wrong by not naming it.
 *
 * And because `admin.` is a DIFFERENT HOST from `app.`, the refresh cookie set
 * here is a different cookie from the customer dashboard's. A staff session
 * cannot be picked up by the customer SPA and vice versa. That separation is
 * the reason the admin portal has its own origin rather than a path on `app.`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ACCESS TOKEN LIVES IN MEMORY, NEVER IN localStorage.
 * ─────────────────────────────────────────────────────────────────────────────
 * A staff access token can read every tenant's fleet. Putting it in
 * `localStorage` would make it readable by any script that reaches this origin
 * and would survive a tab close. It lives in a module variable: gone on reload,
 * and recovered from the httpOnly refresh cookie, which script cannot read.
 *
 * This is why slice 17a mattered — before staff had a refresh token, "in memory
 * only" would have meant re-typing a password on every reload.
 */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`api ${status}`);
    this.name = 'ApiError';
  }
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (accessToken !== null) headers.set('authorization', `Bearer ${accessToken}`);
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(path, {
    ...init,
    headers,
    // The refresh cookie is httpOnly and same-origin; it rides on every request
    // to this origin, which is how a reload recovers a session.
    credentials: 'same-origin',
  });
}

/**
 * Refresh the access token from the httpOnly cookie. Module-scoped promise so
 * ten concurrent 401s produce ONE refresh, not ten — a burst of parallel
 * refreshes rotates the token family repeatedly and the losers get burned.
 */
let inFlightRefresh: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (inFlightRefresh === null) {
    inFlightRefresh = (async () => {
      // 🔴 `request`, NOT `requestWithReplay`, and that is the whole recursion
      // guard. A 401 from the refresh endpoint itself is the end of the
      // session, not something to refresh again — and what stops it recursing
      // is that this line never enters the replay path at all.
      //
      // This comment used to claim a third `_replay = false` argument did the
      // work. It did not: the parameter was never read by `request`. A comment
      // describing a protection that does not run is how the one that does gets
      // refactored away (CLAUDE.md, "never document a guard you have not proven
      // executes"). The parameter is gone; the call shape is the guard.
      const res = await request('/auth/refresh', { method: 'POST' });
      if (!res.ok) throw new ApiError(res.status, null);
      const body = (await res.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return body.accessToken;
    })().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * 🔴 REFRESH AND REPLAY ON 401 — the fix for "Could not load tenants" (17i).
 *
 * THE DEFECT THIS CLOSES. `useStaffAuth` calls `/auth/refresh` exactly ONCE, at
 * boot, and nothing re-refreshes afterwards (no timer, no interval). The access
 * token is short-lived. So a tab left open past its TTL sends a stale bearer on
 * the next request — a refetch, a remount, a navigation back — and gets 401.
 * This client used to throw `ApiError` on any non-ok response with no 401
 * branch at all, and `useTenants`'s retry predicate correctly refuses to retry a
 * 4xx. The screen then failed and STAYED failed until the page was reloaded,
 * because reloading is the only thing that runs the boot refresh again.
 *
 * That is exactly what Mahad reported: intermittent, same account, same screen,
 * and it clears on refresh.
 *
 * NOT A BOOT RACE, and it is worth saying so because that was the first
 * hypothesis. `RequireStaff.tsx:23` renders a loading state and does not mount
 * children while auth is resolving, so a query cannot fire before the boot
 * refresh completes. The trigger is EXPIRY, not ordering.
 *
 * Ported from `web/src/lib/apiClient.ts`, which has had this since slice 04.
 * The admin portal simply never got it — the same capability built in one
 * direction and not the other, which is the shape of the staff-door bug in this
 * same slice.
 */
async function requestWithReplay(path: string, init: RequestInit): Promise<Response> {
  const res = await request(path, init);
  if (res.status !== 401) return res;
  try {
    await refreshAccessToken();
  } catch {
    // The session is genuinely over. Surface the original 401 so the caller
    // sees an auth failure rather than a confusing refresh error.
    setAccessToken(null);
    return res;
  }
  // Replay ONCE. A second 401 is a real answer and falls through.
  return request(path, init);
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await requestWithReplay(path, init);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // A non-JSON error body is not a reason to throw a different error — the
      // status is what the caller acts on.
    }
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/**
 * A request whose SUCCESS has no body — a `204 No Content` writer (slice 17x's
 * password reset is the first).
 *
 * 🔴 `apiJson` CANNOT BE USED FOR THESE. Its final line is `res.json()`, which
 * throws `SyntaxError: Unexpected end of JSON input` on an empty 204 body — so a
 * successful reset would surface to the caller as a parse error and be reported
 * as a failure. This variant shares the refresh-and-replay path and the ApiError
 * mapping for the FAILURE branch, and simply never parses on success.
 */
export async function apiVoid(path: string, init: RequestInit = {}): Promise<void> {
  const res = await requestWithReplay(path, init);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Same reasoning as apiJson: the status is what the caller acts on.
    }
    throw new ApiError(res.status, body);
  }
  // 204: nothing to read, and reading would throw. Return void.
}

/**
 * SLICE 48A — the raw `Response`, for a body that is NOT JSON.
 *
 * The staff CSV export is a stream with a load-bearing HEADER
 * (`X-Report-Row-Count`), so the caller needs the response object itself rather
 * than a parsed body. It goes through `requestWithReplay` like everything else,
 * so an expired access token replays once instead of failing the download.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return requestWithReplay(path, init);
}
